import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { GuardianMark } from "../../brand/marks";
import { MATURITY, type Maturity } from "../../content/guardian-site";

export type Verdict = "allow" | "ask" | "deny";

/** The Authority Seal System (GUA-FINAL-001 §22–31), as marketing components. */

const VERDICT_LABEL: Record<Verdict | "revoked", string> = {
  allow: "ALLOW",
  ask: "ASK",
  deny: "DENY",
  revoked: "REVOKED",
};

export function AuthoritySeal({
  verdict,
  size = "md",
  id,
}: {
  verdict: Verdict | "revoked";
  size?: "sm" | "md" | "lg";
  id?: string;
}) {
  return (
    <span className={`gd-seal gd-seal--${verdict} gd-seal--${size}`} role="img" aria-label={`Guardian seal: ${VERDICT_LABEL[verdict]}`}>
      <span className="gd-seal__ring" aria-hidden="true" />
      <GuardianMark className="gd-seal__mark" />
      <b>{VERDICT_LABEL[verdict]}</b>
      {id ? <small>{id}</small> : null}
    </span>
  );
}

export function MaturityTag({ status, label }: { status: Maturity; label?: string }) {
  return <span className={`gd-avail gd-avail--${status}`} title={MATURITY[status].meaning}>{label ?? MATURITY[status].label}</span>;
}

/** An ASK as one decision object: what Guardian verified, kept apart from what the agent says. */
export function AuthoritySlip({
  agent,
  fields,
  agentSays,
  footer,
}: {
  agent: string;
  fields: { k: string; v: string }[];
  agentSays: string;
  footer?: ReactNode;
}) {
  return (
    <div className="gd-slip">
      <div className="gd-slip__verified">
        <p className="gd-slip__label">Verified by Guardian</p>
        <dl>
          <div><dt>agent</dt><dd>{agent}</dd></div>
          {fields.map((f) => (
            <div key={f.k}><dt>{f.k}</dt><dd>{f.v}</dd></div>
          ))}
        </dl>
      </div>
      <div className="gd-slip__agent">
        <p className="gd-slip__label">Agent says · untrusted</p>
        <p>“{agentSays}”</p>
      </div>
      {footer ? <div className="gd-slip__footer">{footer}</div> : null}
    </div>
  );
}

/** A human grant, shown as authority deliberately given. */
export function AuthorityWrit({
  agent,
  action,
  resource,
  limit,
  expires,
  state,
}: {
  agent: string;
  action: string;
  resource: string;
  limit: string;
  expires: string;
  state: string;
}) {
  return (
    <div className="gd-writ">
      <p className="gd-writ__kicker">Writ of authority</p>
      <p className="gd-writ__declaration">
        I authorize <b>{agent}</b> to <b>{action}</b>.
      </p>
      <dl>
        <div><dt>resource</dt><dd>{resource}</dd></div>
        <div><dt>limit</dt><dd>{limit}</dd></div>
        <div><dt>expires</dt><dd>{expires}</dd></div>
        <div><dt>state</dt><dd>{state}</dd></div>
      </dl>
    </div>
  );
}

export function AuthorityReceipt({
  verdict,
  rows,
}: {
  verdict: Verdict;
  rows: { k: string; v: string }[];
}) {
  return (
    <div className="gd-receipt">
      <AuthoritySeal verdict={verdict} size="sm" />
      <dl>
        {rows.map((r) => (
          <div key={r.k}><dt>{r.k}</dt><dd>{r.v}</dd></div>
        ))}
      </dl>
    </div>
  );
}

/**
 * WAI-ARIA tabs with arrow-key movement. Each tab is a real button; the panel
 * is labeled by its tab.
 */
export function Tabs<T extends { key: string }>({
  label,
  items,
  active,
  onSelect,
  renderTab,
  renderPanel,
  className,
}: {
  label: string;
  items: T[];
  active: string;
  onSelect: (key: string) => void;
  renderTab: (item: T) => ReactNode;
  renderPanel: (item: T) => ReactNode;
  className?: string;
}) {
  const base = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const index = Math.max(0, items.findIndex((i) => i.key === active));
  const current = items[index];

  function onKey(event: KeyboardEvent<HTMLButtonElement>, i: number) {
    const last = items.length - 1;
    const next =
      event.key === "ArrowRight" || event.key === "ArrowDown" ? (i === last ? 0 : i + 1)
      : event.key === "ArrowLeft" || event.key === "ArrowUp" ? (i === 0 ? last : i - 1)
      : event.key === "Home" ? 0
      : event.key === "End" ? last
      : -1;
    if (next < 0) return;
    event.preventDefault();
    const item = items[next];
    if (!item) return;
    onSelect(item.key);
    refs.current[next]?.focus();
  }

  return (
    <div className={className}>
      <div role="tablist" aria-label={label} className="gd-tabs">
        {items.map((item, i) => (
          <button
            key={item.key}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="tab"
            id={`${base}-tab-${item.key}`}
            aria-selected={i === index}
            aria-controls={`${base}-panel`}
            tabIndex={i === index ? 0 : -1}
            className="gd-tab"
            onClick={() => onSelect(item.key)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {renderTab(item)}
          </button>
        ))}
      </div>
      {current ? (
        <div
          role="tabpanel"
          id={`${base}-panel`}
          aria-labelledby={`${base}-tab-${current.key}`}
          className="gd-tabpanel"
          tabIndex={0}
        >
          {renderPanel(current)}
        </div>
      ) : null}
    </div>
  );
}
